import { useEffect, useState } from "react";
import { FormCheck, FormSelect, Modal } from "react-bootstrap";

import { computeDay } from "../../../services/TimeService";
import translate from "../../../services/Translate";
import DstButton from "../../DstButton/DstButton";
import { ProxmoxDeployModalProps, ProxmoxForm } from "./ProxmoxDeployModal.d";
import { deployProxmoxUsers } from "./ProxmoxDeployModal.function";

const _getInitialForm = (cohorts: ProxmoxDeployModalProps["cohorts"]): ProxmoxForm => ({
  expire: `${computeDay(14)}T00:00`,
  enabled: true,
  comment: cohorts.length === 1 ? `${cohorts[0].id} - ${cohorts[0].name}` : "",
});

const ProxmoxDeployModal = ({ show, onHide, onSuccess, userIds, cohorts, language }: ProxmoxDeployModalProps) => {
  const [proxmoxForm, setProxmoxForm] = useState<ProxmoxForm>(_getInitialForm(cohorts));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setProxmoxForm(_getInitialForm(cohorts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.TITLE")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label m-regular" htmlFor="proxmox-expire">
            {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.EXPIRE")}
            <span className="s-regular text-secondary ms-1">
              {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.EXPIRE_DEFAULT")}
            </span>
          </label>
          <input
            type="datetime-local"
            id="proxmox-expire"
            className="form-control"
            value={proxmoxForm.expire}
            onChange={(e) => setProxmoxForm((prev) => ({ ...prev, expire: e.target.value }))}
            min={new Date().toISOString().slice(0, 16)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label m-regular" htmlFor="proxmox-comment">
            {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.COMMENT")}
            <span className="s-regular text-secondary ms-1">
              {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.COMMENT_HINT")}
            </span>
          </label>
          <FormSelect
            id="proxmox-comment"
            value={proxmoxForm.comment}
            onChange={(e) => setProxmoxForm((prev) => ({ ...prev, comment: e.target.value }))}
            disabled={cohorts.length <= 1}
          >
            <option value="" disabled>
              {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.COMMENT_PLACEHOLDER")}
            </option>
            {cohorts.map((cohort, index) => (
              <option key={`proxmox-cohort-choice-${index}`} value={`${cohort.id} - ${cohort.name}`}>
                {cohort.id} - {cohort.name}
              </option>
            ))}
          </FormSelect>
        </div>
        <FormCheck
          id="proxmox-enabled"
          label={
            <span className="m-regular">{translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.ENABLED")}</span>
          }
          checked={proxmoxForm.enabled}
          onChange={(e) => setProxmoxForm((prev) => ({ ...prev, enabled: e.target.checked }))}
        />
      </Modal.Body>
      <Modal.Footer>
        <DstButton
          variant="primary"
          value={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.CONFIRM")}
          loading={loading}
          disabled={loading || !userIds.length || !proxmoxForm.expire || !proxmoxForm.comment}
          clickFunction={() =>
            deployProxmoxUsers(
              userIds,
              language,
              {
                expire: Math.floor(new Date(proxmoxForm.expire).getTime() / 1000),
                enabled: proxmoxForm.enabled,
                comment: proxmoxForm.comment,
              },
              setLoading,
              onSuccess
            )
          }
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ProxmoxDeployModal;
